import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { PlusIcon, TrashIcon, SparklesIcon, FolderIcon } from '@heroicons/react/24/outline'
import { SkillApi, SkillPack, SkillTreeNode } from '../../services/skillApi'
import SkillTree from '../skills/SkillTree'
import { HomeworkApi } from '../../services/homeworkApi'

type LinkRow = {
  id: number
  nodeName?: string
  title?: string
  packName?: string
  relativePath?: string
}

export default function HomeworkSkillAttach({ homeworkId }: { homeworkId: number }) {
  const [links, setLinks] = useState<LinkRow[]>([])
  const [loading, setLoading] = useState(true)
  const [showPicker, setShowPicker] = useState(false)
  const [packs, setPacks] = useState<(SkillPack & { tree: SkillTreeNode[] })[]>([])
  const [selectedPackId, setSelectedPackId] = useState<number | null>(null)
  const [selectedFolder, setSelectedFolder] = useState<SkillTreeNode | null>(null)
  const [saving, setSaving] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  const loadLinks = async () => {
    try {
      setLoading(true)
      const data = await HomeworkApi.listSkillLinks(homeworkId)
      setLinks(data)
      setSelectedIds(new Set())
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được skill')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadLinks()
  }, [homeworkId])

  const someSelected = selectedIds.size > 0
  const allSelected = links.length > 0 && selectedIds.size === links.length
  const selectedPack = packs.find((p) => p.id === selectedPackId) || null

  const openPicker = async () => {
    try {
      setShowPicker(true)
      const data = await SkillApi.listPacksPicker()
      setPacks(data)
      setSelectedPackId(data[0]?.id ?? null)
      setSelectedFolder(null)
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được thư viện skill')
      setShowPicker(false)
    }
  }

  const handleAttach = async () => {
    if (!selectedFolder) {
      toast.error('Chọn một thư mục / file')
      return
    }
    try {
      setSaving(true)
      if (selectedFolder.nodeType === 'FOLDER') {
        await HomeworkApi.addSkillLinks(homeworkId, { parentNodeId: selectedFolder.id })
      } else {
        await HomeworkApi.addSkillLinks(homeworkId, { nodeIds: [selectedFolder.id] })
      }
      toast.success('Đã gắn skill cho BTVN')
      setShowPicker(false)
      await loadLinks()
    } catch (e: any) {
      toast.error(e?.message || 'Gắn skill thất bại')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteSelected = async () => {
    if (!someSelected) return
    try {
      await HomeworkApi.bulkDeleteSkillLinks(homeworkId, [...selectedIds])
      toast.success('Đã xóa')
      await loadLinks()
    } catch (e: any) {
      toast.error(e?.message || 'Xóa thất bại')
    }
  }

  return (
    <div className="rounded-xl border border-m2-outline-variant bg-m2-surface-low p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-m2-primary" />
          <div>
            <h4 className="font-semibold text-m2-on-surface">Skill chấm bài (AI)</h4>
            <p className="text-xs text-m2-on-surface-variant">
              Skill được đưa vào prompt khi AI quét chấm BTVN.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {links.length > 0 && (
            <button
              type="button"
              disabled={!someSelected}
              onClick={() => void handleDeleteSelected()}
              className="inline-flex items-center gap-1 rounded-md border border-red-300 px-2.5 py-1.5 text-xs text-red-700 disabled:opacity-40"
            >
              <TrashIcon className="h-3.5 w-3.5" /> Xóa đã chọn
            </button>
          )}
          <button
            type="button"
            onClick={() => void openPicker()}
            className="inline-flex items-center gap-1 rounded-md border border-m2-primary/30 bg-m2-primary/10 px-2.5 py-1.5 text-xs text-m2-primary"
          >
            <PlusIcon className="h-3.5 w-3.5" /> Gắn skill
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-m2-on-surface-variant">Đang tải...</p>
      ) : links.length === 0 ? (
        <p className="rounded-lg border border-dashed px-3 py-4 text-center text-xs text-m2-on-surface-variant">
          Chưa gắn skill
        </p>
      ) : (
        <ul className="divide-y rounded-lg border bg-white">
          <li className="flex items-center gap-2 bg-m2-surface-high px-3 py-2 text-xs">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() =>
                setSelectedIds(allSelected ? new Set() : new Set(links.map((l) => l.id)))
              }
            />
            <span>{links.length} liên kết</span>
          </li>
          {links.map((link) => (
            <li key={link.id} className="flex items-center gap-2 px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={selectedIds.has(link.id)}
                onChange={() => {
                  const next = new Set(selectedIds)
                  if (next.has(link.id)) next.delete(link.id)
                  else next.add(link.id)
                  setSelectedIds(next)
                }}
              />
              <FolderIcon className="h-4 w-4 text-m2-on-surface-variant" />
              <span className="truncate">
                {link.title || link.nodeName}{' '}
                <span className="text-xs text-m2-on-surface-variant">
                  ({link.packName})
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white p-4 shadow-xl max-h-[80vh] overflow-auto">
            <h4 className="font-semibold mb-3">Chọn skill</h4>
            <div className="flex gap-2 mb-3 flex-wrap">
              {packs.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setSelectedPackId(p.id)
                    setSelectedFolder(null)
                  }}
                  className={`px-2 py-1 rounded text-xs border ${
                    selectedPackId === p.id ? 'bg-m2-primary text-white' : ''
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
            {selectedPack && (
              <SkillTree
                nodes={selectedPack.tree}
                selectedId={selectedFolder?.id}
                onSelectFile={(n) => setSelectedFolder(n)}
                onSelectFolder={(n) => setSelectedFolder(n)}
              />
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="px-3 py-1.5 border rounded text-sm" onClick={() => setShowPicker(false)}>
                Hủy
              </button>
              <button
                type="button"
                disabled={saving}
                className="px-3 py-1.5 bg-m2-primary text-white rounded text-sm"
                onClick={() => void handleAttach()}
              >
                Gắn
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
